import React, { useContext, useMemo } from "react";
import { useTranslation } from "next-i18next";

import Styles from "./AppDownloadBannerV2.module.scss";
import HomepageV2SectionCard from "../section-card/HomepageV2SectionCard";
import AppImg from "../../utils/AppImg";
import HomepageV2Animate from "../animate/HomepageV2Animate";
import DownloadAppAction from "../../common/download-app-qr-modal/DownloadAppAction";
import { AppContext } from "@vahak/core/dist/app-context";
import { IEventTrackerProp } from "../../../types/events";
import { ga4sendEvent, moEngageUserEventTrack } from "@vahak/core/dist/_services/user-analytics.service";
import {
    GA4AppClickSource,
    GA4EventNames,
    GA4HomepageOtherActivityEventValues
} from "@vahak/core/dist/constants/event-names";
import AppleStoreMarkIcon from "@vahak/core/dist/standard-icons/apple-store-mark.svg";
import GooglePlayMarkIcon from "@vahak/core/dist/standard-icons/google-play-mark.svg";
import { APP_STORE_URL } from "@vahak/core/dist/constants";

const PLAY_STORE_URL = "https://6r7cz.app.link/googleplay";

const HOMEPAGE_APP_DOWNLOAD_BANNER = {
    default: "homepage-v2/app-download-banner.png",
    hi: "homepage-v2/app-download-banner-hi.png"
} as const;

function getHomepageAppDownloadBannerImage(locale?: string) {
    return locale === "hi" ? HOMEPAGE_APP_DOWNLOAD_BANNER.hi : HOMEPAGE_APP_DOWNLOAD_BANNER.default;
}

export const APP_DOWNLOAD_BANNER_SECTION_ID = "homepage-download-app";

interface AppDownloadBannerV2Props extends IEventTrackerProp {
    id?: string;
}

const AppDownloadBannerV2 = ({
    trackEvent,
    eventData,
    id = APP_DOWNLOAD_BANNER_SECTION_ID
}: AppDownloadBannerV2Props) => {
    const { t, i18n } = useTranslation("navigation");
    const { userId, name, serviceType, companyId } = useContext(AppContext);
    const bannerImage = useMemo(() => getHomepageAppDownloadBannerImage(i18n.language), [i18n.language]);

    const trackDownload = (store: "app_store" | "play_store") => {
        if (store === "app_store") {
            trackEvent?.(GA4EventNames.WEB_V2.download_appstore, eventData);
        } else {
            trackEvent?.(GA4EventNames.WEB_V2.download_playstore, eventData);
        }

        ga4sendEvent(GA4EventNames.Homepage_other_Activity, {
            value: GA4HomepageOtherActivityEventValues.playstore
        });
        moEngageUserEventTrack("download_app_clicked_web", {
            user_id: userId,
            user_name: name,
            service_type: serviceType,
            source_section: "Homepage v2 app download section",
            store
        });
        ga4sendEvent(GA4EventNames.Download_app_clicked_web, {
            company_id: companyId,
            user_name: name,
            service_type: serviceType,
            source_screen: GA4AppClickSource.homepage_middle_section
        });
    };

    return (
        <HomepageV2SectionCard variant="dark" id={id}>
            <div className={Styles.container}>
                <div className={Styles.banner}>
                    <HomepageV2Animate variant="fade-right" className={Styles.content}>
                        <p className={Styles.eyebrow}>{t("appDownload.eyebrow")}</p>

                        <h2 className={Styles.title}>
                            <span className={Styles.titleLine}>{t("appDownload.titleLine1")}</span>
                            <span className={Styles.titleLine}>{t("appDownload.titleLine2")}</span>
                        </h2>

                        <p className={Styles.subtitle}>{t("appDownload.subtitle")}</p>

                        <div className={Styles.storeButtons}>
                            <DownloadAppAction
                                href={APP_STORE_URL}
                                className={Styles.storeButton}
                                storeUrl={APP_STORE_URL}
                                onClick={() => trackDownload("app_store")}
                                aria-label={t("appDownload.downloadAppStore")}
                            >
                                <span className={Styles.storeIcon}>
                                    <AppleStoreMarkIcon aria-hidden />
                                </span>
                                <span className={Styles.storeText}>
                                    <span className={Styles.storeLabel}>
                                        {t("appDownload.storeButtons.downloadOn")}
                                    </span>
                                    <span className={Styles.storeName}>{t("appDownload.storeButtons.appStore")}</span>
                                </span>
                            </DownloadAppAction>
                           

                            <DownloadAppAction
                                href={PLAY_STORE_URL}
                                className={Styles.storeButton}
                                storeUrl={PLAY_STORE_URL}
                                onClick={() => trackDownload("play_store")}
                                aria-label={t("appDownload.downloadPlayStore")}
                            >
                                <span className={Styles.storeIcon}>
                                    <GooglePlayMarkIcon aria-hidden />
                                </span>
                                <span className={Styles.storeText}>
                                    <span className={Styles.storeLabel}>{t("appDownload.storeButtons.getItOn")}</span>
                                    <span className={Styles.storeName}>{t("appDownload.storeButtons.googlePlay")}</span>
                                </span>
                            </DownloadAppAction>
                        </div>

                        <div className={Styles.stats}>
                            {/* <div className={Styles.stat}>
                                <p className={Styles.statValue}>{t("appDownload.ratings.appStoreValue")}</p>
                                <p className={Styles.statLabel}>{t("appDownload.ratings.appStoreLabel")}</p>
                            </div>

                            <span className={Styles.statDivider} aria-hidden="true" /> */}

                            <div className={Styles.stat}>
                                <p className={Styles.statValue}>{t("appDownload.ratings.downloadsValue")}</p>
                                <p className={Styles.statLabel}>{t("appDownload.ratings.downloadsLabel")}</p>
                            </div>

                            <span className={Styles.statDivider} aria-hidden="true" />

                            <div className={Styles.stat}>
                                <p className={Styles.statValue}>{t("appDownload.ratings.playStoreValue")}</p>
                                <p className={Styles.statLabel}>{t("appDownload.ratings.playStoreLabel")}</p>
                            </div>
                        </div>
                    </HomepageV2Animate>

                    <HomepageV2Animate variant="fade-left" delay={150} className={Styles.imageCol}>
                        <AppImg
                            src={bannerImage}
                            alt={t("appDownload.imageAlt")}
                            width={400}
                            height={600}
                            className={Styles.bannerImage}
                        />
                    </HomepageV2Animate>
                </div>
            </div>
        </HomepageV2SectionCard>
    );
};

export default AppDownloadBannerV2;
